/**
 * Application state: persisted preferences (localStorage), shareable URL hash,
 * and a tiny event bus.
 */
import { BOUNDARY_LAYERS, PLAN_BY_NO, BASEMAP_BY_ID, DEFAULT_VIEW, planFrom } from './config.js';

/* ---------------- event bus ---------------- */

const target = new EventTarget();
export const bus = {
  on: (type, fn) => { const h = (e) => fn(e.detail); target.addEventListener(type, h); return () => target.removeEventListener(type, h); },
  emit: (type, detail) => target.dispatchEvent(new CustomEvent(type, { detail })),
};

/* ---------------- preferences ---------------- */

const KEY = 'qh.prefs.v1';
const defaults = () => ({
  plan: 654,
  boundaries: BOUNDARY_LAYERS.filter((b) => b.on).map((b) => b.layerNo),
  opacity: 0.82,
  basemap: null, // null = follow theme (positron / dark)
  view: null,
  labels: true,
  places: true,
  gia: false, // land price layer (Bảng giá đất 2026)
  sddPeriod: null, // land-use plan map period id (null = the current one)
});

function load() {
  try {
    const p = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (!p || typeof p !== 'object') return defaults();
    const d = defaults();
    return {
      plan: p.plan === null || PLAN_BY_NO[p.plan] ? p.plan : d.plan,
      boundaries: Array.isArray(p.boundaries) ? p.boundaries.filter((n) => BOUNDARY_LAYERS.some((b) => b.layerNo === n)) : d.boundaries,
      opacity: Number.isFinite(p.opacity) ? Math.min(1, Math.max(0.15, p.opacity)) : d.opacity,
      basemap: BASEMAP_BY_ID[p.basemap] ? p.basemap : null,
      view: p.view && Number.isFinite(p.view.lat) ? p.view : null,
      labels: p.labels !== false,
      places: p.places !== false,
      gia: p.gia === true,
      sddPeriod: typeof p.sddPeriod === 'string' && /^[A-Z0-9]{4,10}$/.test(p.sddPeriod) ? p.sddPeriod : null,
    };
  } catch { return defaults(); }
}

export const prefs = load();
let saveTimer = 0;
export function savePrefs() {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => { try { localStorage.setItem(KEY, JSON.stringify(prefs)); } catch { /* quota / private mode */ } }, 250);
}

export function theme() { return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light'; }
export function setTheme(t) {
  document.documentElement.dataset.theme = t;
  try { localStorage.setItem('qh.theme', t); } catch { /* ignore */ }
  bus.emit('theme', t);
}
/** Effective basemap: explicit choice, or the theme-matched default. */
export const basemapId = () => prefs.basemap || (theme() === 'dark' ? 'dark' : 'positron');

/* ---------------- recent searches ---------------- */

const RKEY = 'qh.recent.v1';
export function recent() {
  try { const r = JSON.parse(localStorage.getItem(RKEY) || '[]'); return Array.isArray(r) ? r : []; } catch { return []; }
}
export function addRecent(item) {
  if (!item || !Number.isFinite(item.lat)) return;
  const entry = { label: item.label, sub: item.sub || '', lat: +item.lat.toFixed(6), lng: +item.lng.toFixed(6), kind: item.kind || 'place', zoom: item.zoom, t: Date.now() };
  const list = recent().filter((r) => r.label !== entry.label && Math.hypot(r.lat - entry.lat, r.lng - entry.lng) > 2e-5);
  list.unshift(entry);
  try { localStorage.setItem(RKEY, JSON.stringify(list.slice(0, 8))); } catch { /* ignore */ }
}
export function clearRecent() { try { localStorage.removeItem(RKEY); } catch { /* ignore */ } }

/* ---------------- URL hash ----------------
 * #map=16.50/21.02850/105.85420&l=654&b=659,674&bm=liberty&pt=21.028500,105.854200
 */

export function parseHash(hash = location.hash) {
  const out = {};
  const q = new URLSearchParams(hash.replace(/^#/, ''));
  const m = (q.get('map') || '').split('/').map(Number);
  if (m.length >= 3 && m.every(Number.isFinite) && m[0] >= 5 && m[0] <= 22) out.view = { zoom: m[0], lat: m[1], lng: m[2], bearing: m[3] || 0 };
  if (q.has('l')) out.plan = planFrom(q.get('l'));
  if (q.has('b')) out.boundaries = q.get('b').split(',').map(Number).filter((n) => BOUNDARY_LAYERS.some((b) => b.layerNo === n));
  if (q.has('bm') && BASEMAP_BY_ID[q.get('bm')]) out.basemap = q.get('bm');
  if (/^[A-Z0-9]{4,10}$/.test(q.get('ky') || '')) out.sddPeriod = q.get('ky');
  const pt = (q.get('pt') || '').split(',').map(Number);
  if (pt.length === 2 && pt.every(Number.isFinite)) out.pt = { lat: pt[0], lng: pt[1] };
  return out;
}

let hashTimer = 0;
let current = { view: DEFAULT_VIEW, pt: null };

/** The URL hash for the current view / layers / selection (without the leading '#'). */
export function hashState(patch = {}) {
  const c = { ...current, ...patch };
  const v = c.view;
  const parts = [];
  if (v) parts.push(`map=${v.zoom.toFixed(2)}/${v.lat.toFixed(5)}/${v.lng.toFixed(5)}${Math.abs(v.bearing || 0) > 0.5 ? '/' + Math.round(v.bearing) : ''}`);
  parts.push(`l=${prefs.plan ?? 0}`);
  const defB = BOUNDARY_LAYERS.filter((b) => b.on).map((b) => b.layerNo).join(',');
  const b = [...prefs.boundaries].sort().join(',');
  if (b !== defB) parts.push(`b=${b || '-'}`);
  if (prefs.basemap) parts.push(`bm=${prefs.basemap}`);
  if (prefs.plan === 'sdd' && prefs.sddPeriod) parts.push(`ky=${prefs.sddPeriod}`);
  if (c.pt) parts.push(`pt=${c.pt.lat.toFixed(6)},${c.pt.lng.toFixed(6)}`);
  return parts.join('&');
}

export function writeHash(patch) {
  Object.assign(current, patch);
  clearTimeout(hashTimer);
  hashTimer = setTimeout(() => {
    const h = '#' + hashState();
    if (h !== location.hash) history.replaceState(null, '', h);
  }, 300);
}

/** Link to the current state (always up to date, even while the hash update is pending). */
export function shareUrl(patch) {
  return location.origin + '/#' + hashState(patch);
}
